"use client";

import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage } from "@/components/ui/form";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Loader, Mail } from "lucide-react";
export default function CtaSubscribeForUpdates() {
  return <FormProvider className="relative min-h-[30vh] flex items-center justify-center overflow-hidden bg-dark-background text-foreground-dark py-16 px-4" formId="subscribe-updates-cta" sectionName="cta-subscribe-updates" defaultValues={{
    email: ""
  }} validationRules={{
    email: {
      required: true,
      pattern: "email"
    }
  }} submitText="Subscribe" successMessage="Thank you for subscribing! Check your inbox for updates." errorMessage="Failed to subscribe. Please try again.">
      <div className="absolute inset-0 opacity-10 pointer-events-none">
        <div className="w-full h-full bg-[repeating-linear-gradient(45deg,hsl(0,0%,100%)_0px,hsl(0,0%,100%)_2px,transparent_2px,transparent_6px)]" />
      </div>
      <SubscribeForm />
    </FormProvider>;
}
function SubscribeForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    once: true,
    threshold: 0.2
  });
  if (isSubmitted && !submitError) {
    return <div ref={ref} className={`relative z-10 max-w-2xl mx-auto text-center transition-all duration-[var(--duration-fast)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
        <div className="p-6 bg-primary/20 border border-primary rounded-lg backdrop-blur-sm">
          <p className="text-foreground-dark text-lg mb-4">{successMessage}</p>
          <Button onClick={resetForm} variant="outline" className="bg-background/10 hover:bg-background/20 text-foreground-dark border-foreground-dark/30">
            Subscribe Another Email
          </Button>
        </div>
      </div>;
  }
  return <div ref={ref} className={`relative z-10 max-w-2xl mx-auto text-center transition-all duration-[var(--duration-fast)] ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-5"}`}>
      <h2 className="text-3xl md:text-4xl font-display font-bold mb-3 text-foreground-dark">
        Stay Informed with Lab Updates
      </h2>
      <p className="text-foreground-dark/80 text-lg mb-8">
        Get the latest evidence-based explanations and biomarker insights delivered to your inbox.
      </p>

      <Form onSubmit={handleSubmit}>
        <div className="flex flex-col sm:flex-row gap-3 items-start max-w-xl mx-auto">
          <FormField control={control} name="email" rules={{
          required: "Email is required",
          pattern: {
            value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
            message: "Please enter a valid email address"
          }
        }} render={({
          field
        }) => <FormItem className="flex-1 w-full">
                <FormLabel className="sr-only">Email Address</FormLabel>
                <FormControl>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 size-5 text-foreground-dark/60" />
                    <Input type="email" placeholder="you@example.com" className="pl-10 h-12 bg-transparent border-2 border-foreground-dark/30 text-foreground-dark placeholder:text-foreground-dark/50 rounded-lg focus-visible:border-foreground-dark focus-visible:ring-4 focus-visible:ring-primary/30 transition-all duration-[80ms]" {...field} />
                  </div>
                </FormControl>
                <FormMessage />
              </FormItem>} />

          <Button type="submit" disabled={isSubmitting} className="h-12 px-8 bg-accent text-foreground-accent hover:bg-accent-hover active:bg-accent-active font-semibold rounded-lg shadow-md hover:shadow-lg transition-all duration-[80ms] ease-in-out hover:scale-[0.98] active:scale-[0.98] focus-visible:ring-4 focus-visible:ring-accent/30 w-full sm:w-auto">
            {isSubmitting ? <>
                <Loader className="animate-spin size-5" />
                Subscribing...
              </> : "Subscribe"}
          </Button>
        </div>

        {submitError && <div className="mt-4 p-4 bg-destructive/20 border border-destructive/40 rounded-lg backdrop-blur-sm">
            <p className="text-foreground-dark text-sm">{submitError}</p>
          </div>}
      </Form>
    </div>;
}